import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

interface UserSubscription {
  id: string;
  plan_type: string;
  room_limit: number;
  is_active: boolean;
  expires_at?: string;
}

export function useUserSubscription() {
  const [subscription, setSubscription] = useState<UserSubscription | null>(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    const fetchSubscription = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        // First check for existing subscription in database
        const { data, error } = await supabase
          .from('user_subscriptions')
          .select('*')
          .eq('user_id', user.id)
          .eq('is_active', true)
          .order('created_at', { ascending: false })
          .limit(1)
          .single();

        if (data) {
          // User has a subscription in database, use that
          setSubscription(data);
          setLoading(false);
          return;
        }

        // If no subscription found, check if user is admin/moderator
        if (error && error.code === 'PGRST116') {
          const { data: roleData } = await supabase
            .from('user_roles')
            .select('role')
            .eq('user_id', user.id)
            .in('role', ['admin', 'moderator']);

          // If user is admin or moderator with no explicit subscription, give them ventilate
          if (roleData && roleData.length > 0) {
            setSubscription({
              id: 'auto-granted',
              plan_type: 'ventilate',
              room_limit: 999,
              is_active: true,
            });
            setLoading(false);
            return;
          }
        }

        if (error && error.code !== 'PGRST116') {
          console.error('Error fetching subscription:', error);
        }

        // No subscription found and not admin - defaults to free plan
        setSubscription(null);
      } catch (error) {
        console.error('Error fetching subscription:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSubscription();
  }, [user]);

  const getRoomLimit = () => {
    return 999; // All users have unlimited room access
  };

  const getPlanType = () => {
    return subscription?.plan_type || 'free_release';
  };

  const isFreePlan = () => {
    return getPlanType() === 'free_release';
  };

  const isPremium = () => {
    return getPlanType() === 'premium';
  };

  return {
    subscription,
    loading,
    getRoomLimit,
    getPlanType,
    isFreePlan,
    isPremium,
  };
}